import MenuItem from 'apollo-react/components/MenuItem';
import Select from 'apollo-react/components/Select';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getSavedSearchesByEntity } from '../../../../actions/SavedSearchesActions';
import { setLastSelectedSearchName } from '../../../../actions/SavedSearchFiltersActions';
import { ENTITY_NAME_DASHBOARD } from '../../../../constants/SavedSearches';
import { useTranslation } from '../../../../custom-hooks/useTranslation';
import { isEmpty } from '../../../../utils/ramdaUtils';
import DashboardDialog from '../DashboardDialog';

const BigTableEdit = ({ part, tab, onAccept, onCancel }) => {
  const dispatch = useDispatch();
  const savedSearches = useSelector((state) => state.savedSearches);
  const translateString = useTranslation();

  const savedSearchGroups = savedSearches?.[ENTITY_NAME_DASHBOARD]?.groups ?? [];
  const searchConfig = tab?.graph?.savedSearchByEntity;

  const [data, setData] = useState({ part, tab });
  const [entityName, setEntityName] = useState('');
  const [savedSearchName, setSavedSearchName] = useState('');
  const [disabled, setDisabled] = useState(false);

  function handleDataChange(updates) {
    const clonedData = JSON.parse(JSON.stringify(data));

    updates.forEach(({ path, value }) => {
      _.set(clonedData, path, value);
    });

    setData(clonedData);
  }

  useEffect(() => {
    dispatch(getSavedSearchesByEntity(ENTITY_NAME_DASHBOARD));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchConfig) {
      const searches = savedSearches?.[ENTITY_NAME_DASHBOARD]?.searches ?? [];
      const savedSearch = searches.find(
        (search) => search.searchName === searchConfig.savedSearchName
      );
      if (savedSearch) {
        setEntityName(savedSearch.entityName);
        setSavedSearchName(savedSearch.searchName);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchConfig]);

  useEffect(() => {
    if (!isEmpty(savedSearchName)) {
      handleDataChange([
        { path: 'tab.title', value: savedSearchName },
        {
          path: 'tab.graph.savedSearchByEntity',
          value: {
            entityName,
            savedSearchName,
          },
        },
      ]);
      setDisabled(isEmpty(entityName) || isEmpty(savedSearchName));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSearchName]);

  useEffect(() => {
    if (isEmpty(entityName) || isEmpty(savedSearchName)) {
      setDisabled(true);
    }
  }, [entityName, savedSearchName]);

  const handleSearchChange = (e) => {
    switch (e.target.name) {
      case 'entityName':
        setEntityName(e.target.value);
        setSavedSearchName('');
        break;

      case 'savedSearchName':
        setSavedSearchName(e.target.value);
        break;

      default:
    }
  };

  const getSearchGroups = () => {
    if (!isEmpty(savedSearchGroups)) {
      return savedSearchGroups.map((group) => (
        <MenuItem key={group.entityName} value={group.entityName}>
          {group.translatedEntityName}
        </MenuItem>
      ));
    }
    return !isEmpty(entityName) ? <MenuItem value={entityName}>{entityName}</MenuItem> : null;
  };

  const getSavedSearches = () => {
    if (!isEmpty(savedSearchGroups)) {
      const searchGroup = savedSearchGroups.find((group) => group.entityName === entityName);
      if (!_.isNil(searchGroup)) {
        return searchGroup.savedSearches.map((search) => (
          <MenuItem key={search.searchName} value={search.searchName}>
            {search.translatedSearchName}
          </MenuItem>
        ));
      }
    }
    return !isEmpty(savedSearchName) ? (
      <MenuItem value={savedSearchName}>{savedSearchName}</MenuItem>
    ) : null;
  };

  const handleAccept = () => {
    dispatch(
      setLastSelectedSearchName({
        key: tab.id,
        searchName: savedSearchName,
      })
    );
    onAccept(data);
  };

  const cancelButton = {
    label: translateString('Cancel'),
    onClick: onCancel,
  };

  const acceptButton = {
    label: translateString('Accept'),
    onClick: handleAccept,
    disabled,
  };

  return (
    <DashboardDialog
      data={data}
      buttonProps={[cancelButton, acceptButton]}
      onChange={handleDataChange}
      onClose={onCancel}
    >
      <div>
        <Select
          fullWidth
          required
          error={isEmpty(entityName)}
          helperText={isEmpty(entityName) ? translateString('This field is required') : ''}
          label={translateString('Search Group')}
          placeholder={translateString('Select Search Group')}
          name="entityName"
          value={entityName}
          onChange={handleSearchChange}
        >
          {getSearchGroups()}
        </Select>
      </div>
      <div>
        <Select
          fullWidth
          required
          error={isEmpty(savedSearchName)}
          helperText={isEmpty(savedSearchName) ? translateString('This field is required') : ''}
          label={translateString('Saved Search')}
          placeholder={translateString('Select Saved Search')}
          name="savedSearchName"
          value={savedSearchName}
          onChange={handleSearchChange}
        >
          {getSavedSearches()}
        </Select>
      </div>
    </DashboardDialog>
  );
};

export default BigTableEdit;
